import { computed, Ref, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { WebSocketAuthStatus, webSocketStore } from 'stores/web-socket-store'
import { api, FilterSetting, ViewFilter, ViewFilterEntities } from 'boot/axios'
import { AsyncData } from 'components/models'
import { useAuthUserStore } from 'stores/auth-user-store'
import {
  AuthorShortInfo,
  BuzzSentiment,
  CountriesInner,
  QueryInfo,
  WatchlistContent,
  WatchlistItemsToAdd,
  WatchlistsApiFactory,
  WatchlistsInventoryInner,
  ListsInner
} from '@stockpulse/typescript-axios'
import { Auxiliary } from 'src/helper/Auxiliary'
import { ExchangeData, InventorySector, InventoryTitle, KeyEvent, useCommonStore } from 'stores/common-store'
import { getEntityType } from 'src/helper/StringHelper'
import { ListEntityType } from 'stores/list-view-store'
import { SubscriptionId, useRealTimeDataStore } from 'stores/realtime-data-store'

export interface BuzzAlert {
  email: boolean,
  push: boolean,
  popup: boolean
}
export interface KeyEventAlert {
  key_events: number[],
  email: boolean,
  push: boolean,
  popup: boolean
}
export interface Author {
  name: string,
  source: string
}
export interface AuthorAlert {
  authors: Author[],
  email: boolean,
  push: boolean,
  popup: boolean
}
export interface Alerts {
  buzz?: BuzzAlert,
  key_events?: KeyEventAlert,
  authors?: AuthorAlert
}

export interface APIWatchlistFilter {
  name: string,
  filter: FilterSetting[],
  alerts: Alerts
}
export enum ViewType {
  VIEW = 'view',
  ASSET = 'asset'
}

export interface WatchlistView {
  watchlistId: number,
  name: string,
  filter: ViewFilter,
  alerts: Alerts,
  type: ViewType
}

export type WatchlistAuthor = AuthorShortInfo & { watchlistId: number }

export interface WatchlistData {
  id: number;
  n: string;
  titles: Array<BuzzSentiment>;
  authors?: Array<AuthorShortInfo>;
  queries?: Array<QueryInfo>;
  filters?: Array<WatchlistView>
}

export interface APIWatchlistResponse {
  id: number;
  n: string;
  titles: Array<BuzzSentiment>;
  authors?: Array<AuthorShortInfo>;
  queries?: Array<QueryInfo>;
  filters?: Array<APIWatchlistFilter>
}

export enum filterV3FieldNames {
  sector = 'sector',
  group = 'group',
  industry = 'industry',
  subindustry = 'subindustry',
  // TODO exchange could use column 'listed_at'
  exchange = 'pri_exchange.exchange_id',
  list = 'listed_in',
  country = 'country',
  asset = 'id',
  type = 'type',
  keyEvent = 'key_events'
}

export interface UpdateFilterData {
  name: string,
  alerts: Alerts
}

export type FilterData = UpdateFilterData & { filter: FilterSetting[] }

export type WatchlistItemToAdd = WatchlistItemsToAdd & { filters?: FilterData[] }

export type WatchlistContentData = WatchlistContent & { filters?: any[] }

export const useWatchlistStore = defineStore('watchlistStore', () => {
  const webSocket = webSocketStore()
  const authUserStore = useAuthUserStore()
  const commonStore = useCommonStore()
  const realtimeDataStore = useRealTimeDataStore()
  const { authStatus } = storeToRefs(webSocket)
  const { isLoggedIn } = storeToRefs(authUserStore)
  const watchlistsApiFactory = WatchlistsApiFactory(undefined, '/v6', api)

  const isInitialized = ref(false)
  const watchLists: Ref<AsyncData<APIWatchlistResponse[]>> = ref({ loading: true, value: [] })
  const watchList: Ref<AsyncData<WatchlistData>> = ref({ loading: true })
  const subscriptionIds: Ref<SubscriptionId[]> = ref([])

  const onIsLoggedIn = (newValue: boolean) => {
    if (newValue) {
      loadData()
    }
  }

  watch(isLoggedIn, onIsLoggedIn, { immediate: true })

  function emitToWebsocket () {
    emitWatchlistsToWebsocket()
  }

  const views = computed(() => {
    if (watchLists.value.value === undefined || watchLists.value.value?.length === 0) {
      return []
    }
    const foundViews : WatchlistView[] = []
    watchLists.value.value?.forEach(watchList => {
      watchList.filters?.forEach(view => {
        if (!view.filter) {
          return
        }

        const filterView = Auxiliary.parseFilterSettingToViewFilter(view.filter)
        const entityType = getEntityType(filterView)
        foundViews.push({
          watchlistId: watchList.id,
          name: view.name,
          alerts: view.alerts,
          filter: filterView,
          type: entityType === ListEntityType.ASSET ? ViewType.ASSET : ViewType.VIEW
        })
      })
    })

    return foundViews
  })

  watch(() => views, async () => {
    realtimeDataStore.unsubscribeTitleUpdates(subscriptionIds.value)

    if (views.value.length === 0) {
      return
    }
    const titleIds = views.value
      .filter(view => view.type === ViewType.ASSET)
      .map(view => getTitleFromView(view)?.id)
      .filter(id => id !== undefined) as number[]

    if (titleIds.length === 0) {
      return
    }
    subscriptionIds.value.push(realtimeDataStore.subscribeTitleUpdates([...new Set(titleIds)]))
  }, { deep: true })

  const authors = computed(() => {
    if (watchLists.value.value === undefined || watchLists.value.value?.length === 0) {
      return []
    }
    const foundAuthors : WatchlistAuthor[] = []
    watchLists.value.value?.forEach(watchList => {
      watchList.authors?.forEach(author => {
        foundAuthors.push(Object.assign({}, author, { watchlistId: watchList.id }))
      })
    })

    return foundAuthors
  })

  function emitWatchlistsToWebsocket () {
    // Subscribe to Watchlists
    const allWatchLists = watchLists.value.value
    if (!allWatchLists) {
      return
    }
    allWatchLists.forEach(watchlist => {
      webSocket.emitSubscribeWatchlist(watchlist.id, handleWatchlistMessage)
    })
  }

  const onWebsocketOpen = (newValue: string) => {
    if (newValue === WebSocketAuthStatus.AUTHENTICATED) {
      emitToWebsocket()
    }
  }
  watch(authStatus, onWebsocketOpen)
  onWebsocketOpen(authStatus.value)

  function handleWatchlistMessage (data: any, error: any) {
    // TODO implement
    console.log('handleWatchlistMessage', data, error)
  }

  async function loadData (): Promise<void> {
    watchLists.value.loading = true

    const fetchedWatchlists = await fetchWatchlistData()
    watchLists.value = {
      loading: false,
      value: fetchedWatchlists
    }
    if (authStatus.value) {
      emitWatchlistsToWebsocket()
    }

    isInitialized.value = true
  }

  async function fetchWatchlistData (): Promise<APIWatchlistResponse[]|undefined> {
    const response = await api.get('/v6/watchlists', { headers: { Accept: 'application/json, text/plain, */*' } })

    const watchlists: WatchlistsInventoryInner[] = response.data
    const watchlistsContent = []
    for (const watchlist of watchlists) {
      if (!watchlist.id) {
        continue
      }
      const watchlistContent = await fetchWatchlist(watchlist.id)

      watchlistsContent.push(Object.assign({}, watchlistContent, { id: watchlist.id }) as APIWatchlistResponse)
    }

    return watchlistsContent
  }

  async function fetchWatchlist (watchlistId: number): Promise<WatchlistContentData> {
    const response = await api.get(`/v6/watchlists/${watchlistId}`)

    return response.data as WatchlistContentData
  }

  async function removeViewFromWatchlist (watchlistId: number, viewNames: string[], updateWatchlistState = true) {
    const newWatchListContent = await api.delete(`/v6/watchlists/${watchlistId}/filters`, { data: viewNames })
    if (updateWatchlistState) {
      updateWatchlistFromAPIResponse(watchlistId, newWatchListContent.data)
    }
  }

  async function updateView (view : WatchlistView, oldViewName?: string) {
    if (oldViewName) {
      // Delete old View
      await removeViewFromWatchlist(view.watchlistId, [oldViewName], false)
      // Add 'new' View
      const newView = {
        name: view.name,
        filter: Auxiliary.getFilterSettingsListByTitleFilter(view.filter, true),
        alerts: view.alerts
      }
      await addItemToWatchlist(view.watchlistId, { filters: [newView] })
    } else {
      const filterData = {
        filter: Auxiliary.getFilterSettingsListByTitleFilter(view.filter, true),
        alerts: view.alerts
      }
      const response = await api.post(`/v6/watchlists/${view.watchlistId}/filters`, filterData)
      if (response.status !== 200) {
        return
      }
      updateWatchlistView(view)
    }
  }

  function updateWatchlistFromAPIResponse (watchlistId: number, watchlistToUpdate: WatchlistContentData) {
    const watchlistData = Object.assign({}, watchlistToUpdate, { id: watchlistId }) as APIWatchlistResponse
    if (watchLists.value.value === undefined) {
      watchLists.value.value = [watchlistData]
      return
    }
    const index = watchLists.value.value?.findIndex(watchlist => watchlist.id === watchlistId)
    if (index !== -1) {
      watchLists.value.value[index] = watchlistData
    } else {
      watchLists.value.value.push(watchlistData)
    }
  }

  function updateWatchlistView (watchlistView : WatchlistView) {
    if (watchLists.value.value === undefined) {
      return
    }
    const index = watchLists.value.value?.findIndex(watchlist => watchlist.id === watchlistView.watchlistId)
    if (index === undefined || index < 0) {
      return
    }
    const watchList = watchLists.value.value[index]
    const filterIndex = watchList.filters?.findIndex(filter => filter.name === watchlistView.name)
    if (!filterIndex || filterIndex < 0) {
      return
    }

    const filters = watchLists.value.value[index].filters
    if (!filters || !filters[filterIndex]) {
      return
    }
    filters[filterIndex] = {
      name: watchlistView.name,
      filter: Auxiliary.getFilterSettingsListByTitleFilter(watchlistView.filter),
      alerts: watchlistView.alerts
    }
  }

  async function addItemToWatchlist (watchlistId: number, watchlistItemsToAdd: WatchlistItemToAdd) {
    const newWatchListContent = await watchlistsApiFactory.addItemWatchlist(watchlistId, watchlistItemsToAdd)

    updateWatchlistFromAPIResponse(watchlistId, newWatchListContent.data)
  }

  async function removeTitleFromWatchlist (watchlistId:number, titleId:number) {
    const newWatchListContent = await watchlistsApiFactory.removeIdentFromWatchlist(watchlistId, titleId.toString())
    updateWatchlistFromAPIResponse(watchlistId, newWatchListContent.data)
  }

  async function removeAuthorFromWatchlist (watchlistId: number, name: string, source: string) {
    const newWatchListContent = await watchlistsApiFactory.removeAuthorsFromWatchlist(
      watchlistId,
      { authors: [{ name, source }] }
    )
    updateWatchlistFromAPIResponse(watchlistId, newWatchListContent.data)
  }

  function findViewByViewFilter (titleFilter: ViewFilter): WatchlistView | undefined {
    return views.value.find(view => Auxiliary.compareObjects(titleFilter, view.filter) === 0)
  }

  function getTitleFromView (view: WatchlistView) : InventoryTitle|undefined {
    if (view.type !== ViewType.ASSET) {
      return undefined
    }
    const titleId = view.filter.asset[0]
    if (!titleId) {
      return undefined
    }
    return commonStore.getTitleById(titleId)
  }

  function getViewEntitiesByViewFilter (viewFilter : ViewFilter) : ViewFilterEntities {
    const countryEntities = viewFilter.country
      .map(un => commonStore.getCountryByUn(un))
      .filter(country => country !== undefined) as CountriesInner []
    const exchangeEntities = viewFilter.exchange
      .map(exchangeId => commonStore.getExchangeById(exchangeId))
      .filter(exchange => exchange !== undefined) as ExchangeData []
    const sectorEntities = viewFilter.sector
      .map(sectorId => commonStore.getSectorById(sectorId))
      .filter(sector => sector !== undefined) as InventorySector []
    const listEntities = viewFilter.list
      .map(listId => commonStore.getListById(listId))
      .filter(list => list !== undefined) as ListsInner []
    const keyEventEntities = viewFilter.keyEvent
      .map(keyEventId => commonStore.getKeyEventById(keyEventId))
      .filter(keyEvent => keyEvent !== undefined) as KeyEvent []
    return {
      country: countryEntities,
      exchange: exchangeEntities,
      sector: sectorEntities,
      list: listEntities,
      keyEvent: keyEventEntities
    }
  }

  return {
    isInitialized,
    watchList,
    watchLists,
    views,
    authors,
    updateView,
    addItemToWatchlist,
    removeAuthorFromWatchlist,
    removeViewFromWatchlist,
    findViewByViewFilter,
    getTitleFromView,
    getViewEntitiesByViewFilter
  }
})
